import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Card, Container } from 'react-bootstrap';
import '../csscomponent/OurTeam.css';
import { publicAsset } from '../lib/publicPath';
import { getCmsSettings, getCmsTeam } from '../lib/sqlAuth';
import { subscribeCmsUpdates } from '../lib/cmsSync';

const FALLBACK_TEAM = [
  {
    id: 'fallback-saipan',
    name: 'Saipan Naikwadi',
    position: 'Founder & CEO',
    image: '/images/image3.png',
  },
  {
    id: 'fallback-riya',
    name: 'Riya Patil',
    position: 'UI/UX Designer',
    image: '/images/team2.webp',
  },
  {
    id: 'fallback-aditya',
    name: 'Aditya More',
    position: 'Frontend Developer',
    image: '/images/team1.jpeg',
  },
  {
    id: 'fallback-sneha',
    name: 'Sneha Kulkarni',
    position: 'Project Coordinator',
    image: '/images/image2.png',
  },
];

const getCardsPerSlide = () => {
  if (window.innerWidth < 768) return 1;
  if (window.innerWidth < 1200) return 2;
  return 3;
};

const OurTeam = () => {
  const [cardsPerSlide, setCardsPerSlide] = useState(getCardsPerSlide);
  const [startIndex, setStartIndex] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);
  const [intro, setIntro] = useState({
    title: 'Meet Our Team',
    description: 'We strive to do everything so that you can comfortably and productively work in our space and create amazing products and services',
  });

  useEffect(() => {
    const handleResize = () => setCardsPerSlide(getCardsPerSlide());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!teamMembers.length) return undefined;
    const autoSlide = setInterval(() => {
      setStartIndex((prev) => (prev + 1) % teamMembers.length);
    }, 2600);
    return () => clearInterval(autoSlide);
  }, [teamMembers.length]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [teamRes, settingsRes] = await Promise.all([
          getCmsTeam(),
          getCmsSettings({ keys: ['team_intro'] }),
        ]);
        if (!mounted) return;
        const nextTeam = Array.isArray(teamRes?.items) ? teamRes.items.map((member) => ({
          id: member.id,
          name: member.name,
          position: member.role,
          image: member.image,
        })) : [];
        setTeamMembers(nextTeam.length ? nextTeam : FALLBACK_TEAM);
        const content = settingsRes?.settings?.team_intro || {};
        setIntro((prev) => ({
          title: content.title || prev.title,
          description: content.description || prev.description,
        }));
      } catch (_) {
        if (mounted) {
          setTeamMembers(FALLBACK_TEAM);
        }
      }
    };
    load();
    const unsubscribe = subscribeCmsUpdates((payload) => {
      if (!payload?.scope || ['site', 'team'].includes(payload.scope)) {
        load();
      }
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const visibleMembers = useMemo(
    () =>
      Array.from({ length: cardsPerSlide }, (_, offset) => {
        const memberIndex = (startIndex + offset) % teamMembers.length;
        return { ...teamMembers[memberIndex], slot: offset };
      }),
    [cardsPerSlide, startIndex, teamMembers]
  );

  if (!teamMembers.length) return null;

  return (
    <Container className="py-5 our-team-section">
      <h2 className="text-center text-primary fw-bold mb-3">{intro.title}</h2>
      <p className="text-center mb-4 fs-5 text-success">{intro.description}</p>
      <div className="our-team-slider">
        <AnimatePresence initial={false} mode="popLayout">
          {visibleMembers.map((member) => (
            <motion.article
              layout
              key={member.id}
              className="our-team-card-wrap"
              initial={{ opacity: 0, x: 80 }}
              animate={{
                opacity: 1,
                x: 0,
                transition: { duration: 0.45, delay: member.slot * 0.18 },
              }}
              exit={{ opacity: 0, x: -80, transition: { duration: 0.35 } }}
            >
              <Card className="text-center border-0 shadow-sm our-team-card">
                <Card.Img variant="top" src={member.image ? (member.image.startsWith('http') || member.image.startsWith('/uploads/') ? member.image : publicAsset(member.image)) : publicAsset('images/team1.jpeg')} alt={member.name} />
                <Card.Body>
                  <Card.Title>{member.name}</Card.Title>
                  <Card.Text>{member.position}</Card.Text>
                  <div>
                    <i className="fab fa-facebook-f mx-1"></i>
                    <i className="fab fa-twitter mx-1"></i>
                    <i className="fab fa-linkedin-in mx-1"></i>
                  </div>
                </Card.Body>
              </Card>
            </motion.article>
          ))}
        </AnimatePresence>
      </div>
    </Container>
  );
};

export default OurTeam;
